import React, { useEffect, useState } from 'react';
import Mapbox, { MapboxProps } from '../mapbox';
import { Map as MapboxMap } from 'mapbox-gl';
import _isFunction from 'lodash/isFunction';
import { WayPoint, MapboxView } from '@bringg/types';
import MapboxRouteTrackerPlayer from './mapbox-route-tracker-player/mapbox-route-tracker-player';
import MapboxRouteTrackerTimes from './mapbox-route-tracker-times/mapbox-route-tracker-times';
import MapboxRouteTrackerSlider from './mapbox-route-tracker-slider/mapbox-route-tracker-slider';
import useMapboxWayPoints from '../../../custom-hooks/mapbox/use-mapbox-waypoints/use-mapbox-waypoints';
import '../../../styles/mapbox.scss';
import useMapboxRouteInit from '../../../custom-hooks/mapbox/mapbox-route-tracker/use-mapbox-route-init/use-mapbox-route-init';
import { drawProfileImage, updateProfileImagePoint } from './utils/mapbox-profile-image-util';
import { drawRoute } from './utils/mapbox-draw-route-util';
import { fitBoundsByRouteAndWayPoints } from './utils/mapbox-bounds-fit';
import { MarkerData } from 'types/maps.consts';
import { MapboxViews } from '../mapbox-views/mapbox-views';
import { RouteLocation } from 'types/common.consts';

interface Props extends MapboxProps {
	routeLocations: RouteLocation[];
	wayPoints: WayPoint[];
	profileImageUrl: string;
	timeFormat: string;
	translations: Translations;
	driverLocation?: DriverLocation;
	selectedMarker?: MarkerData;
	timeZone?: string;
	mapRef?: React.Ref<HTMLInputElement>;
	mapViews: MapboxView[];
	currentMapboxView: MapboxView;
	changeMapView: (mapboxView: MapboxView) => void;
}
interface Translations {
	noRouteLocations: string;
	liveTracking: string;
	streets: string;
	satellite: string;
}

export type DriverLocation = {
	lat: number;
	lng: number;
};

const ROUTE_ID = 1;
const PRIMARY_COLOR = '#3d8deb';
const DISABLED_COLOR = '#E1E1E1';
const MAIN_STEPS = 30; // as more steps we have - the velocity of the route tracker will be slower

const MapboxRouteTracker: React.FC<Props> = props => {
	const {
		onLoaded: userOnLoaded,
		routeLocations: initialRoute,
		driverLocation,
		wayPoints,
		profileImageUrl,
		timeFormat,
		translations,
		timeZone,
		mapRef,
		mapViews,
		currentMapboxView,
		changeMapView,
		...otherProps
	} = props;
	const [map, setMap] = useState<MapboxMap>();
	const [routeIndex, setRouteIndex] = useState(0);
	const [mapBoxStyleLoaded, setMapBoxStyleLoaded] = useState(false);

	const { route, routeFeature, profileImagePoint } = useMapboxRouteInit(
		initialRoute,
		routeIndex,
		MAIN_STEPS,
		driverLocation
	);
	const { drawWayPoints } = useMapboxWayPoints(map, wayPoints, timeFormat, timeZone);

	useEffect(() => {
		setMapBoxStyleLoaded(false);
	}, [otherProps.mapboxOptions.style]);

	map?.on('style.load', () => {
		!mapBoxStyleLoaded && setMapBoxStyleLoaded(true);
	});

	useEffect(() => {
		if (!map || !mapBoxStyleLoaded) {
			return;
		}
		drawRoute(map, { id: ROUTE_ID, locations: route }, PRIMARY_COLOR);
		drawWayPoints();
	}, [map, route, wayPoints, mapBoxStyleLoaded]);

	useEffect(() => {
		if (!map || !mapBoxStyleLoaded) {
			return;
		}
		fitBoundsByRouteAndWayPoints({ map, route, wayPoints, driverLocation });
	}, [map, route, driverLocation, wayPoints, mapBoxStyleLoaded]);

	useEffect(() => {
		if (!map || !mapBoxStyleLoaded || !driverLocation) {
			return;
		}
		onUpdateProfileImagePoint();
	}, [driverLocation]);

	useEffect(() => {
		if (!map || !profileImagePoint || !mapBoxStyleLoaded) {
			return;
		}

		async function handleProfileImage() {
			await drawProfileImage({
				map,
				profileImagePoint,
				profileImageUrl,
				driverLocation,
				route,
				routeIndex,
				color: PRIMARY_COLOR
			});
		}
		handleProfileImage();
	}, [map, profileImageUrl, profileImagePoint, mapBoxStyleLoaded]);

	const onLoaded = (map: MapboxMap) => {
		setMapBoxStyleLoaded(true);
		setMap(map);

		if (_isFunction(userOnLoaded)) {
			userOnLoaded(map);
		}
	};

	const onUpdateProfileImagePoint = () =>
		updateProfileImagePoint({ map, profileImagePoint, route, routeIndex, driverLocation });

	const isRouteTrackerDisabled = route.length < 2 || !!driverLocation;

	return (
		<div className="mapbox-route-tracker" ref={mapRef}>
			<MapboxViews
				className="route-tracker-map-views"
				mapViews={mapViews}
				currentMapboxView={currentMapboxView}
				changeMapView={changeMapView}
				translations={translations}
			/>
			{<Mapbox {...otherProps} onLoaded={onLoaded} />}
			<MapboxRouteTrackerSlider
				routeLocations={route}
				routeIndex={routeIndex}
				onChangeRouteIndex={setRouteIndex}
				timeFormat={timeFormat}
				timeZone={timeZone}
				disabled={isRouteTrackerDisabled}
			/>
			{isRouteTrackerDisabled ? (
				<span className="no-route-locations">
					{driverLocation ? translations.liveTracking : translations.noRouteLocations}
				</span>
			) : (
				<MapboxRouteTrackerTimes routeLocations={route} timeFormat={timeFormat} timeZone={timeZone} />
			)}
			<MapboxRouteTrackerPlayer
				routeProps={{ routeLocations: route, routeIndex, routeFeature, onRouteIndexChange: setRouteIndex }}
				profileImageProps={{ onUpdateProfileImagePoint }}
				mainSteps={MAIN_STEPS}
				colorProps={{ primaryColor: PRIMARY_COLOR, disabledColor: DISABLED_COLOR }}
				disabled={isRouteTrackerDisabled}
			/>
		</div>
	);
};

export default MapboxRouteTracker;
